import "./setup/devtools/memoization";

import { QueryStatus } from "./services";
import {
  loadModule,
  useDynamicScript,
} from "./services/moduleFederation/helpers";
import { getFeatureFlags } from "./next/hooks/featureFlags";

if (process.env.NODE_ENV === "test") {
  throw new Error(
    "When testing, @granular/fabric-core should always be mocked instead of imported.",
  );
}

/**
 * Warnings
 */

import { showDeprecationWarnings } from "./helpers/warnings";
showDeprecationWarnings();

/**
 * Components
 */
export { default as FabricShell } from "./components/FabricShell";
export { default as Loading } from "./components/Loading";
export { default as NotFound } from "./components/NotFound";
export { default as Error } from "./components/Error";
export {
  usePrimaryNavItems,
  useSecondaryNavItems,
  OperationDropdown,
} from "./components/Nav.helpers";

export { default as EnsureCoreData } from "./components/EnsureCoreData";
export type { FabricApp, RequiredCoreData } from "./components/EnsureCoreData";

/**
 * Portals
 */
export * from "./components/NavPortal";

/**
 * Constants
 */
export { NAV_NOTIFICATIONS_INSIGHTS_KEY } from "./constants";

/**
 * Hooks
 */
export {
  useAuthentication,
  useCrops,
  useCurrentOperation,
  useFabricDepCheck,
  useLogFabricProvidedDependencies,
  useFarms,
  useFarmsWith,
  useFields,
  useFieldsWith,
  useLogAppVersion,
  useOperations,
  useOperationsWith,
  useUser,
  useLocale,
  useFabricState,
  useFabricValue,
  useSetFabricState,
  useUserCan,
  // Undocumented
  useFabricReducer,
  getAppDeps,
  getSharedFabricDeps,
} from "./hooks";

/**
 * Redux Helpers + Fabric Status and Activities
 */
export { fabricStatus, fabricActivities } from "./redux";

/**
 * Internationalization
 */
export {
  DEFAULT_LOCALE as defaultLocale,
  SUPPORTED_LOCALES as supportedLocales,
  DEFAULT_COUNTRY as defaultCountry,
  DEFAULT_REGION as defaultRegion,
  SUPPORTED_COUNTRIES as supportedCountries,
} from "./constants";

export {
  countryData,
  countryList,
  getBrowserLanguage,
  getBrowserLocale,
  getCountryDataFor,
  getCountryFromUrl,
  getCustomerResources,
  standardizeLocale,
} from "./i18n";

export type { CountryData } from "./i18n";

/**
 * Legacy
 */
export { segmentService } from "./legacy/segment/SegmentService";

/**
 * Utilities
 */
export {
  convertSquareMetresToAcres,
  convertSquareMetresToHectares,
  convertDataToDataById,
  convertDataToDataByGranularId,
  getGranularRuntimeEnvName,
  getEnvironmentFromUrl,
  getPlatform,
  getRuntimeEnvName,
  regexForGetOperationFromUrl,
  getUserType,
  getRegexMatchingFromUrl,
  getRoleFromPermissions,
  userCan,
} from "./helpers";

export type { ActionForUserCan } from "./helpers";

/**
 * Types
 */
export type { EmptyObj, ChildrenProp, NonUndefined } from "./types";

/**
 * Services
 */
export { getFeatureFlags };
export { getHistory, getAppName, tripInsightsDataDirtyFlag } from "./services";
export type { QueryStatus };

export {
  bootstrapAxiosInstance,
  getAuthToken,
  getAuthInfoHeader,
  getLocaleHeader,
  getOperationHeader,
  getUserHeader,
} from "./services/granularAxios";

export * from "./services/moduleFederation";

/**
 * Models
 */
export * from "./models";

/**
 * Next
 */
export * from "./next";

// IMPORTANT: The typing of `_containerOnly` may seem unnecessary but it is actually necessary for the propagation of the deprecation notice.

/**
 * Exports for use only in the Container.
 */
export const _containerOnly: {
  useDynamicScript: (url: string) => {
    isReady: boolean;
    itFailedError: ErrorEvent | null;
    /**
     * @deprecated Please use itFailedError instead.
     */
    itFailed: boolean;
  };
  loadModule: typeof loadModule;
} = {
  useDynamicScript,
  loadModule,
};
